import { useEffect, useRef } from 'react';
import { css, cx } from '@linaria/atomic';
import type { ScheduleActionType, SchedulingType } from '@frontend/api-ai-settings';
import { i18next, useTranslation } from '@frontend/i18n';
import { ContainedIcon, Icon, IconName } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { AiContainedIcon, gradientAnimationStyles } from '@frontend/styled-icons';
import { theme } from '@frontend/theme';
import { Avatar, BannerNotification, Button, Text } from '@frontend/design-system';

const aITypingIndicatorDivStyle8 = css`
  box-shadow: ${theme.shadows.light};
  border-radius: ${theme.borderRadius.medium};
`;

const aITypingIndicatorDivStyle9 = css`
  border-width: 1px;
  border-style: solid;
  border-color: transparent;
  background: ${`
          linear-gradient(${theme.colors.white}, ${theme.colors.white}) padding-box,
          linear-gradient(90deg, #1950A3 0%, #0D5D59 100%) border-box
        `};
`;

const aITypingIndicatorContainedIconStyle10 = css`
  padding-top: 2px;
  padding-right: 2px;
  padding-bottom: 2px;
  padding-left: 2px;
  border-radius: ${theme.borderRadius.small};
`;

const userTypingIndicatorDivStyle11 = css`
  border: ${`1px solid ${theme.colorsV2.blue50()}`};
`;

const callEndedIndicatorDivStyle12 = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(2)};

  &::before,
  &::after {
    content: '';
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 0%;
    height: 1px;
    background-color: ${theme.colorsV2.neutral30()};
  }
`;

const callEndedIndicatorTextStyle13 = css`
  white-space: nowrap;
`;

const scheduleEventCardIconStyle14 = css`
  color: var(--schedule-event-card--icon-inline-icon-color);
`;

export interface TranscriptTurn {
  id: string;
  speaker: 'user' | 'ai';
  text: string;
  timestamp: Date;
  isPartial?: boolean;
}

export interface ScheduleEventTurn {
  id: string;
  type: 'schedule_event';
  actionType: ScheduleActionType;
  schedulingType?: SchedulingType;
  timestamp: Date;
  phoneNumber?: string;
  appointmentRequestId?: string;
  timezone?: string;
  startTime?: string;
  appointmentId?: string;
}

export type ConversationTurn = TranscriptTurn | ScheduleEventTurn;

interface ConversationTranscriptProps {
  transcripts: ConversationTurn[];
  userInitials?: string;
  userName?: string;
  isAISpeaking?: boolean;
  isUserSpeaking?: boolean;
  showYourTurn?: boolean;
  callEnded?: boolean;
  settingsChangedBanner?: string | null;
  onViewCalendar?: (turn: ScheduleEventTurn) => void;
  onViewAppointmentRequest?: (requestId: string) => void;
  // Show text confirmation card on schedule event if true else fallback to Schedule Event card
  showScheduleTextConfirmation?: boolean;
}

interface TurnCardProps {
  turn: TranscriptTurn;
  userInitials?: string;
  userName?: string;
}

const isScheduleEventTurn = (turn: ConversationTurn): turn is ScheduleEventTurn => {
  return 'type' in turn && turn.type === 'schedule_event';
};

const containerStyle = css`
  padding-top: ${theme.spacing(2)};
  padding-right: ${theme.spacing(2)};
  padding-bottom: ${theme.spacing(2)};
  padding-left: ${theme.spacing(2)};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
`;

const turnContentStyle = css`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  padding-top: ${theme.spacing(2)};
  padding-right: ${theme.spacing(2)};
  padding-bottom: ${theme.spacing(2)};
  padding-left: ${theme.spacing(2)};
  gap: ${theme.spacing(1)};
  box-shadow: ${theme.shadows.light};
  border-radius: ${theme.borderRadius.medium};
`;

const scheduleEventCardStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: var(--fabric-space-padding-sm);
  padding-right: var(--fabric-space-padding-md);
  padding-bottom: var(--fabric-space-padding-sm);
  padding-left: var(--fabric-space-padding-md);
  border-radius: var(--fabric-radius-md);
  background: var(--fabric-color-surface-canvas);
  box-shadow: ${theme.shadows.light};
`;

const scheduleEventLeftContentStyle = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xs);
`;

const confirmationTextSentCardStyle = css`
  display: flex;
  align-items: center;
  padding-top: var(--fabric-space-padding-xs);
  padding-right: var(--fabric-space-padding-md);
  padding-bottom: var(--fabric-space-padding-xs);
  padding-left: var(--fabric-space-padding-md);
  border-radius: var(--fabric-radius-md);
  background: var(--fabric-color-accent-neutral-subtle);
`;

const confirmationTextSentContentStyle = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xs);
`;

const typingIndicatorStyle = css`
  display: inline-flex;
  gap: 4px;

  span {
    animation-duration: 1.4s;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
    animation-name: bounce;
    display: inline-block;

    &:nth-of-type(1) {
      animation-delay: 0s;
    }

    &:nth-of-type(2) {
      animation-delay: 0.2s;
    }

    &:nth-of-type(3) {
      animation-delay: 0.4s;
    }
  }

  @keyframes bounce {
    0%,
    60%,
    100% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-8px);
    }
  }
`;

const AITurnCard = ({ turn }: { turn: TranscriptTurn }) => {
  return (
    <div className={turnContentStyle}>
      <AiContainedIcon iconName='aiweave-assistant' size={40} />
      <Text>{turn.text}</Text>
    </div>
  );
};

const AITypingIndicator = () => {
  return (
    <div className={cx(aITypingIndicatorDivStyle8, aITypingIndicatorDivStyle9)}>
      <div className={turnContentStyle} css={gradientAnimationStyles()}>
        <ContainedIcon iconName='aiweave-assistant' size={40} className={aITypingIndicatorContainedIconStyle10} />
        <div className={typingIndicatorStyle}>
          <Text as='span' color={'light'}>
            ●
          </Text>
          <Text as='span' color={'light'}>
            ●
          </Text>
          <Text as='span' color={'light'}>
            ●
          </Text>
        </div>
      </div>
    </div>
  );
};

const UserTypingIndicator = () => {
  return (
    <div className={cx(turnContentStyle, userTypingIndicatorDivStyle11)}>
      <Avatar size='medium' />
      <div className={typingIndicatorStyle}>
        <Text as='span' color={'primary'}>
          ●
        </Text>
        <Text as='span' color={'primary'}>
          ●
        </Text>
        <Text as='span' color={'primary'}>
          ●
        </Text>
      </div>
    </div>
  );
};

const YourTurnIndicator = () => {
  const { t } = useTranslation('ai-receptionist');
  return (
    <div className={turnContentStyle}>
      <Avatar size='medium' />
      <Text color={'light'}>{t('Ask Sam anything...')}</Text>
    </div>
  );
};

const CallEndedIndicator = () => {
  return (
    <div className={callEndedIndicatorDivStyle12}>
      <Text color='subdued' className={callEndedIndicatorTextStyle13}>
        Call Ended
      </Text>
    </div>
  );
};

const getScheduleCardMetadata = (actionType: ScheduleActionType) => {
  switch (actionType) {
    case 'RESCHEDULE':
      return {
        iconName: 'calendar-rescheduled' as IconName,
        iconColor: 'var(--fabric-color-accent-eggplant-base)',
        title: i18next.t('Appointment Rescheduled', { ns: 'ai-receptionist' }),
      };
    case 'CANCEL':
      return {
        iconName: 'calendar-canceled' as IconName,
        iconColor: 'var(--fabric-color-status-error-default)',
        title: i18next.t('Removed from Schedule', { ns: 'ai-receptionist' }),
      };
    case 'SCHEDULE':
    case 'REQUEST':
    case 'CONFIRM':
    default:
      return {
        iconName: 'calendar-add' as IconName,
        iconColor: 'var(--fabric-color-accent-blue-base)',
        title: i18next.t('Added to Schedule', { ns: 'ai-receptionist' }),
      };
  }
};

const ConfirmationTextSentCard = ({ phoneNumber }: { phoneNumber?: string }) => {
  const { t } = useTranslation('ai-receptionist');
  return (
    <div className={confirmationTextSentCardStyle}>
      <div className={confirmationTextSentContentStyle}>
        <Icon name='message-outgoing' size={24} />
        {phoneNumber ? (
          <Text>{t('Confirmation Text Sent to {{phoneNumber}}', { phoneNumber: formatPhoneNumber(phoneNumber) })}</Text>
        ) : (
          <Text>{t('Confirmation Text Sent to phone number')}</Text>
        )}
      </div>
    </div>
  );
};

export const AppointmentRequestConfirmationCard = ({
  turn,
  onViewAppointmentRequest,
}: {
  turn: ScheduleEventTurn;
  onViewAppointmentRequest?: (requestId: string) => void;
}) => {
  const { t } = useTranslation('ai-receptionist');

  return (
    <div css={scheduleEventCardStyle}>
      <div css={scheduleEventLeftContentStyle}>
        <Icon name='calendar-requested' size={24} css={{ color: 'var(--fabric-color-seaweed-50)' }} />
        <Text>{t('Appointment Request Created')}</Text>
      </div>
      <Button
        variant='tertiary'
        size='small'
        trackingId='air-demo-view-appointment-request-view'
        onClick={() => onViewAppointmentRequest?.(turn.appointmentRequestId ?? '')}
        disabled={!onViewAppointmentRequest}
      >
        {t('View')}
      </Button>
    </div>
  );
};

export const ScheduleEventCard = ({
  turn,
  onViewCalendar,
}: {
  turn: ScheduleEventTurn;
  onViewCalendar?: (eventTurn: ScheduleEventTurn) => void;
}) => {
  const { title, iconName, iconColor } = getScheduleCardMetadata(turn.actionType);
  const { t } = useTranslation('ai-receptionist');
  return (
    <div className={scheduleEventCardStyle}>
      <div className={scheduleEventLeftContentStyle}>
        <Icon
          name={iconName}
          size={24}
          className={scheduleEventCardIconStyle14}
          style={
            {
              '--schedule-event-card--icon-inline-icon-color': iconColor,
            } as any
          }
        />
        <Text>{title}</Text>
      </div>
      <Button variant='tertiary' size='small' onClick={() => onViewCalendar?.(turn)} disabled={!onViewCalendar}>
        {t('View Calendar')}
      </Button>
    </div>
  );
};

const UserTurnCard = ({ turn }: Omit<TurnCardProps, 'turn'> & { turn: TranscriptTurn }) => {
  return (
    <div className={turnContentStyle}>
      <Avatar size='medium' />
      <Text>{turn.text}</Text>
    </div>
  );
};

export const ConversationTranscript = ({
  transcripts,
  userInitials,
  userName,
  isAISpeaking = false,
  isUserSpeaking = false,
  showYourTurn = false,
  callEnded = false,
  settingsChangedBanner = null,
  onViewCalendar,
  onViewAppointmentRequest,
  showScheduleTextConfirmation = false,
}: ConversationTranscriptProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new transcripts arrive or speaking state changes
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcripts, isAISpeaking, isUserSpeaking, showYourTurn, callEnded, settingsChangedBanner]);

  return (
    <div className={containerStyle} ref={scrollRef}>
      {transcripts.map((turn) =>
        isScheduleEventTurn(turn) ? (
          showScheduleTextConfirmation || turn.schedulingType === 'TRUELARK_SCHEDULING' ? (
            <ConfirmationTextSentCard key={turn.id} phoneNumber={turn.phoneNumber} />
          ) : turn.schedulingType === 'WEAVE_REQUEST_SCHEDULING' ? (
            <AppointmentRequestConfirmationCard
              key={turn.id}
              turn={turn}
              onViewAppointmentRequest={onViewAppointmentRequest}
            />
          ) : turn.schedulingType === 'WEAVE_DIRECT_SCHEDULING' ? (
            <ScheduleEventCard key={turn.id} turn={turn} onViewCalendar={onViewCalendar} />
          ) : null
        ) : turn.speaker === 'ai' ? (
          <AITurnCard key={turn.id} turn={turn} />
        ) : (
          <UserTurnCard key={turn.id} turn={turn} userInitials={userInitials} userName={userName} />
        )
      )}
      {isAISpeaking && <AITypingIndicator />}
      {!isAISpeaking && !isUserSpeaking && showYourTurn && <YourTurnIndicator />}
      {isUserSpeaking && <UserTypingIndicator />}
      {callEnded && <CallEndedIndicator />}
      {settingsChangedBanner && <BannerNotification status='info' message={settingsChangedBanner} />}
    </div>
  );
};
